import { z } from 'zod';
import {
  ChartAntiCd38SplitSchema,
  ChartArchetypeDistributionSchema,
  ChartDecisionMakingStyleSchema,
  ChartDeescalationOrderSchema,
  ChartEndpointImportanceSchema,
  ChartPatientMixSchema,
  ChartRegimenChoiceDriversSchema,
  ChartRegimenTypeMixSchema,
  ChartTolerabilityRankingSchema,
  ChartTopConstraintsSchema,
  ChartTopInformationSourcesSchema,
} from './segment-chart-data';
import { SegmentCardSchema } from './segment-card';
import {
  ExecutiveSummaryPayloadSchema,
  ExecutiveSummaryStatusSchema,
} from './segment-executive-summary';

export const SegmentAggregatedValueSchema = z.union([
  z.string(),
  z.record(z.string(), z.unknown()),
  z.null(),
]);

const SegmentCardColumnSchema = SegmentCardSchema.nullable().optional();

export const SegmentAggregatedDataSchema = z
  .object({
    id: z.number().int().nonnegative(),
    segmentName: z.string().min(1),
    coreIdentityPracticeContext: z.string().nullable().optional(),
    treatmentPhilosophyArchetype: z.string().nullable().optional(),
    regimenChoicePatterns1l: z.string().nullable().optional(),
    durationDeescalationSequencing: z.string().nullable().optional(),
    sharedDecisionMakingStyle: z.string().nullable().optional(),
    evidenceInformationPreferences: z.string().nullable().optional(),
    emotionalDriversFriction: z.string().nullable().optional(),
    chartArchetypeDistribution: ChartArchetypeDistributionSchema.nullable().optional(),
    chartRegimenTypeMix: ChartRegimenTypeMixSchema.nullable().optional(),
    chartAntiCd38Split: ChartAntiCd38SplitSchema.nullable().optional(),
    chartDecisionMakingStyle: ChartDecisionMakingStyleSchema.nullable().optional(),
    chartTopConstraints: ChartTopConstraintsSchema.nullable().optional(),
    chartPatientMix: ChartPatientMixSchema.nullable().optional(),
    chartTolerabilityRanking: ChartTolerabilityRankingSchema.nullable().optional(),
    chartEndpointImportance: ChartEndpointImportanceSchema.nullable().optional(),
    chartDeescalationOrder: ChartDeescalationOrderSchema.nullable().optional(),
    chartTopInformationSources: ChartTopInformationSourcesSchema.nullable().optional(),
    chartRegimenChoiceDrivers: ChartRegimenChoiceDriversSchema.nullable().optional(),
    syntheticAudiencePrompt: z.string().nullable().optional(),
    cardPracticeModelRole: SegmentCardColumnSchema,
    cardPatientMix: SegmentCardColumnSchema,
    cardInstitutionalEnvironment: SegmentCardColumnSchema,
    cardArchetype: SegmentCardColumnSchema,
    cardRiskBenefitStance: SegmentCardColumnSchema,
    cardEvidenceOrientation: SegmentCardColumnSchema,
    card1lIntensityRegimen: SegmentCardColumnSchema,
    cardAntiCd38Behavior: SegmentCardColumnSchema,
    cardDurationSequencing: SegmentCardColumnSchema,
    cardPatientDecisionStyle: SegmentCardColumnSchema,
    cardInformationPreferences: SegmentCardColumnSchema,
    cardConstraints: SegmentCardColumnSchema,
    cardMotivatorsChangeTriggers: SegmentCardColumnSchema,
  })
  .catchall(SegmentAggregatedValueSchema);

export type SegmentAggregatedData = z.infer<typeof SegmentAggregatedDataSchema>;

/** Cards-only response schema — excludes large narrative fields from API payload. */
export const SegmentAggregatedCardsResponseSchema = z.object({
  id: z.number().int().nonnegative(),
  segmentName: z.string().min(1),
  cardPracticeModelRole: SegmentCardColumnSchema,
  cardPatientMix: SegmentCardColumnSchema,
  cardInstitutionalEnvironment: SegmentCardColumnSchema,
  cardArchetype: SegmentCardColumnSchema,
  cardRiskBenefitStance: SegmentCardColumnSchema,
  cardEvidenceOrientation: SegmentCardColumnSchema,
  card1lIntensityRegimen: SegmentCardColumnSchema,
  cardAntiCd38Behavior: SegmentCardColumnSchema,
  cardDurationSequencing: SegmentCardColumnSchema,
  cardPatientDecisionStyle: SegmentCardColumnSchema,
  cardInformationPreferences: SegmentCardColumnSchema,
  cardConstraints: SegmentCardColumnSchema,
  cardMotivatorsChangeTriggers: SegmentCardColumnSchema,
  chartArchetypeDistribution: ChartArchetypeDistributionSchema.nullable().optional(),
  chartRegimenTypeMix: ChartRegimenTypeMixSchema.nullable().optional(),
  chartAntiCd38Split: ChartAntiCd38SplitSchema.nullable().optional(),
  chartDecisionMakingStyle: ChartDecisionMakingStyleSchema.nullable().optional(),
  chartTopConstraints: ChartTopConstraintsSchema.nullable().optional(),
  chartPatientMix: ChartPatientMixSchema.nullable().optional(),
  chartTolerabilityRanking: ChartTolerabilityRankingSchema.nullable().optional(),
  chartEndpointImportance: ChartEndpointImportanceSchema.nullable().optional(),
  chartDeescalationOrder: ChartDeescalationOrderSchema.nullable().optional(),
  chartTopInformationSources: ChartTopInformationSourcesSchema.nullable().optional(),
  chartRegimenChoiceDrivers: ChartRegimenChoiceDriversSchema.nullable().optional(),
  executiveSummary: ExecutiveSummaryPayloadSchema.nullable().optional(),
  executiveSummaryStatus: ExecutiveSummaryStatusSchema.optional(),
});

export type SegmentAggregatedCardsResponse = z.infer<typeof SegmentAggregatedCardsResponseSchema>;

export namespace SegmentAggregatedDataAPI {
  export namespace GetBySegmentId {
    export const requestParamsSchema = z.object({
      segmentId: z.coerce.number().int().positive(),
    });

    export const responseBodySchema = z.object({
      item: SegmentAggregatedCardsResponseSchema,
    });

    export type Response = z.infer<typeof responseBodySchema>;
  }

  export namespace GetCardCitations {
    export const requestParamsSchema = z.object({
      segmentId: z.coerce.number().int().positive(),
    });

    export const requestQuerySchema = z.object({
      cardKey: z.string().min(1),
    });

    const citationTranscriptSchema = z.object({
      transcriptId: z.number(),
      transcriptName: z.string(),
    });

    const citationSectionSchema = z.object({
      sectionKey: z.string(),
      sectionTitle: z.string(),
      transcripts: z.array(citationTranscriptSchema),
    });

    export const responseBodySchema = z.object({
      sections: z.array(citationSectionSchema),
    });

    export type CitationTranscript = z.infer<typeof citationTranscriptSchema>;
    export type CitationSection = z.infer<typeof citationSectionSchema>;
    export type Response = z.infer<typeof responseBodySchema>;
  }
}
